/**
 * Ce qui a change entre deux versions d'un profil.
 *
 * POURQUOI CE MODULE EXISTE. Le tiroir d'historique affichait, a chaque version,
 * la liste complete des codes d'habilitation. Un profil d'exploitation en porte
 * pres de deux cents : on obtenait deux murs de pastilles identiques, et la seule
 * question qu'on vient poser ici - qu'est-ce qui a change - restait sans reponse.
 *
 * Le code, lui, n'est plus consultable depuis l'interface : c'est le libelle qui
 * s'affiche.
 *
 * Extrait du composant pour etre verifiable sans rendu.
 */

import i18n from '../i18n';

/** La cle de comparaison : l'identifiant, ou le code a defaut. */
export function cleHabilitation(h) {
  if (!h) return null;
  // Le repli sur le code n'est pas cosmetique : sans lui, deux habilitations
  // sans identifiant partageraient la cle `undefined`, et le diff annoncerait
  // des ajouts et des retraits qui n'ont pas eu lieu.
  return h.habilitationTrackingId || h.codeHabilitation || null;
}

/**
 * Ce que l'administrateur lit.
 *
 * JAMAIS DE REPLI SUR LE CODE. Le montrer ici rouvrirait par la fenetre ce que
 * l'ecran des habilitations vient de fermer par la porte.
 */
export function libelleLisible(h) {
  return (h && h.libelleHabilitation) || i18n.t('profils:historique.libelleAbsent');
}

const parLibelle = (a, b) => libelleLisible(a).localeCompare(libelleLisible(b), 'fr');

/**
 * Compare deux versions.
 *
 * `comparable` distingue deux situations que rien ne separait : « il n'y avait
 * rien avant » (creation : tout est un ajout) et « on ne sait pas ce qu'il y
 * avait avant » (la version precedente est sur une autre page du tiroir). Les
 * confondre presenterait deux cents habilitations comme deux cents ajouts - un
 * historique faux, et spectaculaire.
 */
export function diffHabilitations(precedentes, courantes) {
  const comparable = Array.isArray(precedentes);
  const apres = Array.isArray(courantes) ? courantes : [];

  if (!comparable) {
    return { comparable: false, ajoutees: [], retirees: [], inchangees: [...apres].sort(parLibelle) };
  }

  const clesAvant = new Set(precedentes.map(cleHabilitation).filter(Boolean));
  const clesApres = new Set(apres.map(cleHabilitation).filter(Boolean));

  return {
    comparable: true,
    ajoutees: apres.filter((h) => !clesAvant.has(cleHabilitation(h))).sort(parLibelle),
    retirees: precedentes.filter((h) => !clesApres.has(cleHabilitation(h))).sort(parLibelle),
    inchangees: apres.filter((h) => clesAvant.has(cleHabilitation(h))).sort(parLibelle),
  };
}

/**
 * Le numero d'une version, deduit du total et de la position.
 *
 * <p>POURQUOI IL FAUT LE CALCULER. `ProfilResponse` ne porte aucun champ
 * `version` : le serveur n'en envoie pas. Le tiroir faisait donc
 * {@code h.version ?? idx + 1}, ce qui numerotait a l'envers - la requete trie
 * `ORDER BY createdAt DESC`, donc l'entree la PLUS RECENTE se retrouvait a
 * l'indice 0 et recevait le numero 1.
 *
 * <p>Les degats depassaient le numero affiche. Une version 1 se compare a un
 * ensemble vide : la derniere modification s'annoncait comme une creation, et les
 * cent-soixante-dix-neuf habilitations du profil paraissaient toutes ajoutees.
 * L'ajout reel - une habilitation - etait noye dans le lot.
 *
 * <p>La plus recente porte donc le plus grand numero, la plus ancienne porte 1.
 * `idx` est la position DANS LA PAGE, d'ou le decalage par la page.
 */
export function numeroVersion(total, page, pageSize, idx) {
  const rang = page * pageSize + idx;
  const n = total - rang;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * La version qui precede, c'est-a-dire l'element SUIVANT.
 *
 * <p>La liste est ordonnee du plus recent au plus ancien : ce qui precede une
 * entree est donc juste apres elle. L'ancienne version cherchait un element dont
 * le numero valait `version - 1` ; comme aucun element ne porte de numero, elle
 * ne trouvait jamais rien et n'a jamais pu fonctionner.
 *
 * <p>Rend `null` quand la page s'arrete avant : c'est ce `null` qui rend le diff
 * non comparable, plutot que de le faire mentir.
 */
export function precedenteDansPage(items, idx) {
  if (!Array.isArray(items)) return null;
  return items[idx + 1] || null;
}

/**
 * Faut-il marquer les habilitations ajoutees d'une pastille « nouveau » ?
 *
 * NON A LA CREATION, et c'est tout l'interet de la question. A la version 1,
 * chaque habilitation est un ajout : marquer les cent-soixante-dix-neuf d'un
 * « N » ne distinguerait rien de rien. Le titre du groupe dit deja « A la
 * creation ».
 *
 * NON NON PLUS quand le diff n'est pas comparable : la version precedente etant
 * hors de la page, « ajoutees » est vide et il n'y a rien a marquer - mais le
 * dire ici evite que l'appelant ait a y penser.
 */
export function marquerNouveau(version, comparable) {
  return comparable === true && version !== 1;
}

/**
 * Ce qu'une entree d'historique doit MONTRER, et ce qu'elle doit seulement compter.
 *
 * <p>LE DEFAUT : CHAQUE VERSION REPETAIT TOUTE LA LISTE. Le tiroir rendait trois
 * groupes - ajoutees, retirees, inchangees - et le troisieme portait le reste, soit
 * presque tout. Sur un profil de cent-soixante-dix-neuf habilitations et dix
 * versions par page, cela fait pres de dix-huit cents pastilles a faire defiler
 * pour trouver la seule qui a bouge. L'information utile etait noyee dans sa propre
 * repetition.
 *
 * <p>LA REGLE : on montre CE QUI A CHANGE, on compte le reste. Le reste est
 * repliable, donc disponible, mais il ne s'impose plus.
 *
 * <p>DEUX CAS N'ONT PAS DE DELTA, et tombent entiers dans le repliable. A la
 * creation, tout est un ajout : ce n'est pas un changement a lire, c'est un point de
 * depart. Et quand la version precedente est hors de la page, aucun delta n'est
 * calculable - le tiroir le dit par ailleurs.
 *
 * @returns {{ajoutees: Array, retirees: Array, pliable: Array, libellePliable: string}}
 */
export function groupesHistorique(d, version) {
  const vide = { ajoutees: [], retirees: [] };
  // Libelle lu a l'appel (profils.json, historique.pliable), accorde au nombre en
  // anglais ; le francais garde la forme unique d'origine.
  const libelle = (cle, liste) => i18n.t(`profils:historique.pliable.${cle}`, { count: (liste || []).length });

  if (!d || d.comparable !== true) {
    return { ...vide, pliable: d?.inchangees || [], libellePliable: libelle('habilitations', d?.inchangees) };
  }

  if (version === 1) {
    return { ...vide, pliable: d.ajoutees, libellePliable: libelle('creation', d.ajoutees) };
  }

  return {
    ajoutees: d.ajoutees,
    retirees: d.retirees,
    pliable: d.inchangees,
    libellePliable: libelle('inchangees', d.inchangees),
  };
}
